// Runtime and build-time configuration (Vite: VITE_*; legacy: window.env REACT_APP_* for runtime injection)

const stripQuotes = (value: string) => value.replace(/^['"]|['"]$/g, "");

function getFromWindow(
  win: Record<string, unknown> | undefined,
  viteKey: string,
  legacyKey: string
): string {
  if (!win) return "";
  const v = win[viteKey] ?? win[legacyKey];
  if (v === undefined || v === null) return "";
  const s = String(v);
  if (s.includes("${")) return "";
  return stripQuotes(s);
}

function getBuildEnv(viteKey: string): string {
  const v = (import.meta.env as unknown as Record<string, string | undefined>)[
    viteKey
  ];
  return v ? stripQuotes(v) : "";
}

/**
 * Vite injects VITE_* at build time. For runtime overrides, use window.env with
 * either VITE_* or legacy REACT_APP_* keys.
 */
function getEnvValue(viteKey: string, legacyKey: string): string {
  const w =
    typeof window !== "undefined" ? (window as unknown as { env?: Record<string, unknown> }).env : undefined;
  if (w) {
    const fromWin = getFromWindow(w, viteKey, legacyKey);
    if (fromWin) return fromWin;
  }
  return getBuildEnv(viteKey) || getBuildEnv(legacyKey);
}

export interface RuntimeConfig {
  REACT_APP_BASE_PATH: string;
  REACT_APP_OIDC_AUTHORITY: string;
  REACT_APP_OIDC_AUTHORIZATION_ENDPOINT: string;
  REACT_APP_OIDC_CLIENT_ID: string;
  REACT_APP_OIDC_END_SESSION_ENDPOINT: string;
  REACT_APP_OIDC_INTROSPECTION_ENDPOINT: string;
  REACT_APP_OIDC_JWKS_URI: string;
  REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI: string;
  REACT_APP_OIDC_REDIRECT_URI: string;
  REACT_APP_OIDC_RESPONSE_TYPE: string;
  REACT_APP_OIDC_SCOPE: string;
  REACT_APP_OIDC_TOKEN_ENDPOINT: string;
  REACT_APP_OIDC_USERINFO_ENDPOINT: string;
}

const getRuntimeConfig = (): RuntimeConfig => {
  const w =
    typeof window !== "undefined" ? (window as unknown as { env?: Record<string, unknown> }).env : undefined;

  if (w) {
    const oidcVite = getFromWindow(w, "VITE_OIDC_AUTHORITY", "REACT_APP_OIDC_AUTHORITY");
    if (oidcVite) {
      return {
        REACT_APP_BASE_PATH: getFromWindow(w, "VITE_BASE_PATH", "REACT_APP_BASE_PATH"),
        REACT_APP_OIDC_AUTHORITY: oidcVite,
        REACT_APP_OIDC_AUTHORIZATION_ENDPOINT: getFromWindow(
          w,
          "VITE_OIDC_AUTHORIZATION_ENDPOINT",
          "REACT_APP_OIDC_AUTHORIZATION_ENDPOINT"
        ),
        REACT_APP_OIDC_CLIENT_ID: getFromWindow(w, "VITE_OIDC_CLIENT_ID", "REACT_APP_OIDC_CLIENT_ID"),
        REACT_APP_OIDC_END_SESSION_ENDPOINT: getFromWindow(
          w,
          "VITE_OIDC_END_SESSION_ENDPOINT",
          "REACT_APP_OIDC_END_SESSION_ENDPOINT"
        ),
        REACT_APP_OIDC_INTROSPECTION_ENDPOINT: getFromWindow(
          w,
          "VITE_OIDC_INTROSPECTION_ENDPOINT",
          "REACT_APP_OIDC_INTROSPECTION_ENDPOINT"
        ),
        REACT_APP_OIDC_JWKS_URI: getFromWindow(w, "VITE_OIDC_JWKS_URI", "REACT_APP_OIDC_JWKS_URI"),
        REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI: getFromWindow(
          w,
          "VITE_OIDC_POST_LOGOUT_REDIRECT_URI",
          "REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI"
        ),
        REACT_APP_OIDC_REDIRECT_URI: getFromWindow(w, "VITE_OIDC_REDIRECT_URI", "REACT_APP_OIDC_REDIRECT_URI"),
        REACT_APP_OIDC_RESPONSE_TYPE:
          getFromWindow(w, "VITE_OIDC_RESPONSE_TYPE", "REACT_APP_OIDC_RESPONSE_TYPE") || "code",
        REACT_APP_OIDC_SCOPE: getFromWindow(w, "VITE_OIDC_SCOPE", "REACT_APP_OIDC_SCOPE"),
        REACT_APP_OIDC_TOKEN_ENDPOINT: getFromWindow(
          w,
          "VITE_OIDC_TOKEN_ENDPOINT",
          "REACT_APP_OIDC_TOKEN_ENDPOINT"
        ),
        REACT_APP_OIDC_USERINFO_ENDPOINT: getFromWindow(
          w,
          "VITE_OIDC_USERINFO_ENDPOINT",
          "REACT_APP_OIDC_USERINFO_ENDPOINT"
        ),
      };
    }
  }

  return {
    REACT_APP_BASE_PATH: getEnvValue("VITE_BASE_PATH", "REACT_APP_BASE_PATH"),
    REACT_APP_OIDC_AUTHORITY: getEnvValue("VITE_OIDC_AUTHORITY", "REACT_APP_OIDC_AUTHORITY"),
    REACT_APP_OIDC_AUTHORIZATION_ENDPOINT: getEnvValue(
      "VITE_OIDC_AUTHORIZATION_ENDPOINT",
      "REACT_APP_OIDC_AUTHORIZATION_ENDPOINT"
    ),
    REACT_APP_OIDC_CLIENT_ID: getEnvValue("VITE_OIDC_CLIENT_ID", "REACT_APP_OIDC_CLIENT_ID"),
    REACT_APP_OIDC_END_SESSION_ENDPOINT: getEnvValue(
      "VITE_OIDC_END_SESSION_ENDPOINT",
      "REACT_APP_OIDC_END_SESSION_ENDPOINT"
    ),
    REACT_APP_OIDC_INTROSPECTION_ENDPOINT: getEnvValue(
      "VITE_OIDC_INTROSPECTION_ENDPOINT",
      "REACT_APP_OIDC_INTROSPECTION_ENDPOINT"
    ),
    REACT_APP_OIDC_JWKS_URI: getEnvValue("VITE_OIDC_JWKS_URI", "REACT_APP_OIDC_JWKS_URI"),
    REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI: getEnvValue(
      "VITE_OIDC_POST_LOGOUT_REDIRECT_URI",
      "REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI"
    ),
    REACT_APP_OIDC_REDIRECT_URI: getEnvValue("VITE_OIDC_REDIRECT_URI", "REACT_APP_OIDC_REDIRECT_URI"),
    REACT_APP_OIDC_RESPONSE_TYPE: getEnvValue("VITE_OIDC_RESPONSE_TYPE", "REACT_APP_OIDC_RESPONSE_TYPE") || "code",
    REACT_APP_OIDC_SCOPE: getEnvValue("VITE_OIDC_SCOPE", "REACT_APP_OIDC_SCOPE"),
    REACT_APP_OIDC_TOKEN_ENDPOINT: getEnvValue("VITE_OIDC_TOKEN_ENDPOINT", "REACT_APP_OIDC_TOKEN_ENDPOINT"),
    REACT_APP_OIDC_USERINFO_ENDPOINT: getEnvValue("VITE_OIDC_USERINFO_ENDPOINT", "REACT_APP_OIDC_USERINFO_ENDPOINT"),
  };
};

export const runtimeConfig = getRuntimeConfig();

export const {
  REACT_APP_BASE_PATH,
  REACT_APP_OIDC_AUTHORITY,
  REACT_APP_OIDC_AUTHORIZATION_ENDPOINT,
  REACT_APP_OIDC_CLIENT_ID,
  REACT_APP_OIDC_END_SESSION_ENDPOINT,
  REACT_APP_OIDC_INTROSPECTION_ENDPOINT,
  REACT_APP_OIDC_JWKS_URI,
  REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI,
  REACT_APP_OIDC_REDIRECT_URI,
  REACT_APP_OIDC_RESPONSE_TYPE,
  REACT_APP_OIDC_SCOPE,
  REACT_APP_OIDC_TOKEN_ENDPOINT,
  REACT_APP_OIDC_USERINFO_ENDPOINT,
} = runtimeConfig;
